import { usePrefersReducedMotion } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

const DURATION_MS = 1100;

const THRESHOLD = 0.5;

const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3);

export const useCountUp = <T extends HTMLElement>(target: number) => {
  const ref = useRef<T>(null);
  const [value, setValue] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      setValue(target);
      return;
    }

    const element = ref.current;
    if (!element) return;

    let frame = 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();

        const start = performance.now();
        const tick = (now: number) => {
          const progress = Math.min((now - start) / DURATION_MS, 1);
          setValue(Math.round(target * easeOutCubic(progress)));
          if (progress < 1) frame = requestAnimationFrame(tick);
        };

        frame = requestAnimationFrame(tick);
      },
      { threshold: THRESHOLD },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [target, prefersReducedMotion]);

  return { ref, value };
};
